import { BeatSequencer } from '@/story/sequencer';
import { CORRIDOR_CUES, CORRIDOR_FLAGS, CORRIDOR_IDS, SERVICE_CORRIDOR_BEAT } from '@/story/act1/serviceCorridor';
import { BAYS, DOOR_AT, SERVICE_SPAWN, serviceCorridorSolids } from '@/world/serviceCorridor';
import { bakeServiceLightGrid, serviceCover } from '@/world/serviceCorridorStealth';
import { CorridorDirector } from '@/world/corridorDirector';
import { StealthSystem } from '@/systems/stealth';
import type { Interactable } from '@/systems/interaction';
import type { BeatPartCapabilities, BeatPartLoop, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';

const DOOR_LEAF = 'personnel-door-leaf';

/**
 * Act 1 beat 4, *The Service Corridor*: three patrols, the dog, the mat and
 * the door, the washdown, the scent.
 *
 * Until session 84 this was a branch in `main.ts` plus lines in seven other
 * places there (D-231). Where each piece now runs, against where it ran:
 *
 * - The director's step, the door collider's pose and the patrol target's
 *   `moveTo` ran right after the controller; they still do, in `afterMove`,
 *   which exists for them — the interaction system samples the moved target
 *   on the same tick.
 * - The place flags and the "covered near the walker" latch ran there too;
 *   they now run in `fixedStep`, after interaction events, still before
 *   `story.update` in the same tick.
 * - The nozzle nudge staged `waterRunning` from the interaction-event loop;
 *   `fixedStep` stages it now, same tick, before the story.
 * - The stealth field is stepped after `physics.step` (`afterPhysics`), as
 *   before and for the reason `main.ts` gives there: the eye is a Rapier ray.
 * - The dog's panting loop was the host's (`updateDogPant`); it is the
 *   part's render step now.
 */
function createServiceCorridorPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 4 / part 1: the host offered no world capability');
  const doorSolid = serviceCorridorSolids().find((solid) => solid.name === DOOR_LEAF);
  if (!doorSolid) throw new Error(`beat 4 needs the ${DOOR_LEAF} solid`);

  const story = new BeatSequencer(SERVICE_CORRIDOR_BEAT);
  // Forward-declared: the director reports `dragged` through the beat's own
  // cue channel, and that channel is this part's `handleCue`.
  let handleCue: (cue: { id: string }) => void = () => undefined;
  const director = new CorridorDirector(world.scene, {
    onOfferPatrol: () => world.interaction.setEnabled(CORRIDOR_IDS.patrol, true),
    onOfferNozzle: () => world.interaction.setEnabled(CORRIDOR_IDS.nozzle, true),
    onCue: (cue) => handleCue({ id: cue }),
    // Nails on sealed concrete, and quiet. It is the only thing that carries
    // round the corner she spends stage 6 behind, so it is mixed to be heard
    // over the corridor's own fluorescent buzz rather than to be dramatic.
    onDogStep: (at) => world.playOneShot('dog-claws-concrete', { gain: 0.7, position: at }),
    // Louder than the claws by design. `dog-snuff` gates at −51 dBFS RMS, the
    // quietest thing in the registry, and it is the most important sound in
    // the beat — the gain is where that gets corrected, not the definition,
    // because the definition is what the offline measurement is taken from.
    onDogCast: (at) => world.playOneShot('dog-snuff', { gain: 1.0, position: at }),
    // Her weight on the mat: the latch knocking in its keeper. Beat 6's race
    // clang, small and high — the same steel, a fraction of the mass.
    onMatTry: (at) => world.playOneShot('race-clang', { gain: 0.22, position: at, detune: 900 }),
  });
  // The room's dressed door is a still; the director's leaf is the one that swings.
  const dressedDoor = world.scene.getObjectByName('personnel-door');
  if (dressedDoor) dressedDoor.visible = false;
  // The room leaves the leaf's collider out (`rooms/service.ts`); this is it,
  // and it follows the swinging mesh every fixed step.
  const doorCollider = world.colliders.add(doorSolid);
  // These exact watcher objects are passed to stealth and mutated by Patrol.step();
  // copying them would freeze the cones at their offstage positions.
  const stealth = new StealthSystem(bakeServiceLightGrid(), serviceCover(), director.patrols.map((patrol) => patrol.watcher), world.lineOfSight);
  const interactables: readonly Interactable[] = [
    { id: CORRIDOR_IDS.patrol, at: { x: 13.6, y: 0.45, z: 9.0 }, verbs: ['observe'], mapGlyph: 'person', reach: 6, duration: 3, enabled: false },
    { id: CORRIDOR_IDS.nozzle, at: { x: -2.2, y: 0.1, z: 5.55 }, verbs: ['nudge'], reach: 0.65, enabled: false },
  ];
  /** Latched: she has been in a bay within 6 m of the walker at least once. */
  let coveredNearWalker = false;
  /** The dog's panting. See `updatePant` below for why it is a loop. */
  let pant: BeatPartLoop | null = null;
  let disposed = false;

  handleCue = (cue): void => {
    director.handle(cue as Parameters<CorridorDirector['handle']>[0]);
    if (cue.id === CORRIDOR_CUES.waterRunning) world.interaction.setEnabled(CORRIDOR_IDS.nozzle, false);
    // He calls it off, and the beat's own comment on `WASH_PATIENCE` is why this
    // sound matters more than any other single event in the corridor: it says
    // the dog was never the danger. **It comes from the man, not the animal** —
    // positioned at the handler, wherever on his round he happens to be, so the
    // player hears which of the two was in charge the whole time.
    if (cue.id === CORRIDOR_CUES.whistleItBack) {
      const handler = director.patrols[2].state;
      world.playOneShot('handler-whistle', { gain: 0.8, position: { x: handler.x, y: 1.55, z: handler.z } });
    }
  };

  /**
   * The dog's panting, which is the only continuous thing it emits.
   *
   * A loop rather than a repeated one-shot because it is presence, not an
   * event: it is under everything else while the animal is on stage and gone
   * the moment it is not — the one channel that tells the player *the dog is
   * still working* while she is behind something looking at a wall. Started and
   * stopped on visibility rather than on a stage, because the director already
   * owns exactly one truth about whether there is a dog in the room.
   */
  const updatePant = (): void => {
    const dog = director.dogDebug;
    if (!dog.visible) {
      pant?.stop();
      pant = null;
      return;
    }
    const at = { x: dog.x, y: 0.4, z: dog.z };
    // **`!active` and not just `null`**: `playLoop` hands back a permanently
    // inactive handle whenever the context is not running yet, and audio only
    // unlocks on the player's first gesture. A `??=` would latch that silent
    // handle for the whole of the dog's visibility (caught by independent
    // review). 0.55 is quiet on purpose: Rosalie's breathing is the stealth UI
    // (spec §8), and a pant that competes with it would replace the meter the
    // player is actually reading.
    if (!pant || !pant.active) pant = world.playLoop('dog-pant', { gain: 0.55, position: at });
    pant.setPosition(at);
  };

  return {
    story,
    interactables,
    stealth,
    get fade(): number { return director.fade; },
    // The legacy prompt list had no corridor term, so the verb prompt stayed
    // up over a capture's black and the closing one. Every other part hides
    // it past half-black; so does this one now (D-231).
    get promptSuppressed(): boolean { return !disposed && director.fade > 0.5; },
    afterMove({ dt, player, motion }): void {
      // Patrols, dog, pressure door/water, then scent: one fixed-step
      // encounter pipeline, before Rapier rebuilds its ray-query structure.
      // A capture in progress respawns her from inside this call (`moveSelf`,
      // which it was handed in `afterPhysics`).
      director.step(dt, { x: player.x, y: player.y, z: player.z, grounded: motion?.grounded ?? true }, story.debug.stageId);
      world.colliders.setPose(doorCollider, director.doorColliderPose);
      const nearest = director.patrols.filter((patrol) => patrol.state.phase !== 'parked')
        .sort((a, b) => Math.hypot(a.state.x - player.x, a.state.z - player.z) - Math.hypot(b.state.x - player.x, b.state.z - player.z))[0];
      if (nearest) world.interaction.moveTo(CORRIDOR_IDS.patrol, { x: nearest.state.x, y: 0.45, z: nearest.state.z });
    },
    fixedStep({ player, raiseFlag, nudged }): void {
      const insideBay = BAYS.some((bay) => player.x >= bay.minX && player.x <= bay.maxX && player.z >= bay.minZ && player.z <= bay.maxZ);
      const walker = director.patrols[0].state;
      if (insideBay && Math.hypot(walker.x - player.x, walker.z - player.z) <= 6) coveredNearWalker = true;
      if (player.z >= SERVICE_SPAWN.z + 3.0) raiseFlag(CORRIDOR_FLAGS.inCorridor);
      if (walker.roundsCompleted >= 1 && coveredNearWalker) raiseFlag(CORRIDOR_FLAGS.firstPassed);
      const washdown = BAYS[2];
      if (washdown && player.x >= washdown.minX && player.x <= washdown.maxX && player.z >= washdown.minZ && player.z <= washdown.maxZ) raiseFlag(CORRIDOR_FLAGS.atWashdown);
      if (director.noseIsDown) raiseFlag(CORRIDOR_FLAGS.noseDown);
      if (director.trailIsBroken) raiseFlag(CORRIDOR_FLAGS.trailBroken);
      if (player.x >= DOOR_AT.x + 0.3) raiseFlag(CORRIDOR_FLAGS.throughDoor);
      if (nudged?.(CORRIDOR_IDS.nozzle) === true) handleCue({ id: CORRIDOR_CUES.waterRunning });
    },
    afterPhysics({ dt, player, moveSelf }): void {
      const snapshot = stealth.step(dt, { x: player.x, y: player.y, z: player.z });
      if (moveSelf === undefined) return;
      director.capture(snapshot, player, moveSelf);
    },
    handleCue(cue): void {
      handleCue(cue);
    },
    renderStep(frameDt): void {
      if (disposed) return;
      director.update(frameDt);
      updatePant();
    },
    diagnostics() {
      return {
        kind: 'corridor',
        corridor: {
          patrols: director.patrols.map((patrol) => ({ id: patrol.id, x: patrol.state.x, z: patrol.state.z, facing: patrol.state.facing, phase: patrol.state.phase, roundsCompleted: patrol.state.roundsCompleted })),
          tracker: { ...director.dogState },
          dog: { ...director.dogDebug },
          doorAngle: director.doorAngle,
          matTries: director.matTries,
          wetBox: director.wetBox,
          stealth: stealth.snapshot(),
        },
      };
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      pant?.stop();
      pant = null;
      world.colliders.remove(doorCollider);
      director.dispose();
    },
  };
}

export const SERVICE_CORRIDOR_PART_MODULE: BeatPartModuleDefinition = {
  beat: 4,
  part: 1,
  room: 'service',
  create: createServiceCorridorPartModule,
};
